import React, { useState, useEffect } from "react";
import AccountApiClient from "../../services/AccountApiClient";

interface AddEditBusinessProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  business?: {
    id: string;
    name: string;
  } | null;
}

const AddEditBusiness: React.FC<AddEditBusinessProps> = ({
  isOpen,
  onClose,
  onSave,
  business,
}) => {
  const [businessName, setBusinessName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldShowPanel, setShouldShowPanel] = useState(false);

  const isEdit = business !== null && business !== undefined;

  // Handle slide animation
  useEffect(() => {
    if (isOpen) {
      // Add the open class on the next tick to trigger the transition
      const timer = setTimeout(() => {
        setShouldShowPanel(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setShouldShowPanel(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (isEdit && business) {
        setBusinessName(business.name);
      } else {
        setBusinessName("");
      }
      setError(null);
    }
  }, [isOpen, isEdit, business]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!businessName.trim()) {
      setError("Business name is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await AccountApiClient.addBusiness(businessName.trim());
      onSave();
      onClose();
    } catch (error: any) {
      setError(error.message || "Failed to save business");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setBusinessName("");
      setError(null);
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            opacity: shouldShowPanel ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
          onClick={handleClose}
        />
      )}

      {/* Slide Panel */}
      {isOpen && (
        <div
          className="slide-panel"
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "400px",
            height: "100vh",
            backgroundColor: "white",
            boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.15)",
            zIndex: 1001,
            transform: shouldShowPanel ? "translateX(0)" : "translateX(100%)",
            transition: "transform 0.3s ease",
            display: "flex",
            flexDirection: "column",
          }}
        >
        {/* Header */}
        <div
          className="slide-panel-header"
          style={{
            padding: "1.5rem",
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ margin: 0, color: "#111827" }}>
            {isEdit ? "Edit Business" : "Add Business"}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: "#6b7280",
              padding: "0.25rem",
            }}
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div
          className="slide-panel-content"
          style={{
            flex: 1,
            padding: "1.5rem",
            overflowY: "auto",
          }}
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
              <label
                htmlFor="businessName"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                  color: "#374151",
                }}
              >
                Business Name *
              </label>
              <input
                type="text"
                id="businessName"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Enter business name"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  fontSize: "1rem",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#667eea";
                  e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#d1d5db";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {error && (
              <div
                className="error-message"
                style={{
                  color: "#dc2626",
                  backgroundColor: "#fef2f2",
                  padding: "0.75rem",
                  borderRadius: "0.375rem",
                  marginBottom: "1rem",
                  border: "1px solid #fecaca",
                }}
              >
                {error}
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div
          className="slide-panel-footer"
          style={{
            padding: "1.5rem",
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
            display: "flex",
            gap: "0.75rem",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: "0.75rem 1.5rem",
              border: "1px solid #d1d5db",
              borderRadius: "0.375rem",
              backgroundColor: "white",
              color: "#374151",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: "500",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !businessName.trim()}
            style={{
              padding: "0.75rem 1.5rem",
              border: "none",
              borderRadius: "0.375rem",
              backgroundColor: loading || !businessName.trim() ? "#9ca3af" : "#667eea",
              color: "white",
              cursor: loading || !businessName.trim() ? "not-allowed" : "pointer",
              fontSize: "0.875rem",
              fontWeight: "500",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (!loading && businessName.trim()) {
                e.currentTarget.style.backgroundColor = "#5a67d8";
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && businessName.trim()) {
                e.currentTarget.style.backgroundColor = "#667eea";
              }
            }}
          >
            {loading ? "Saving..." : isEdit ? "Update" : "Add"}
          </button>
        </div>
        </div>
      )}
    </>
  );
};

export default AddEditBusiness; 